import axios, { AxiosError } from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { API_BASE_URL } from '../config/amplify';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

// Inject Cognito JWT on every request
api.interceptors.request.use(async (config) => {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (token) config.headers.Authorization = `Bearer ${token}`;
  } catch {
    // Not logged in — let the request proceed (public endpoints)
  }
  return config;
});

// Normalise API responses
api.interceptors.response.use(
  (res) => res,
  (err: AxiosError<{ error: string }>) => {
    const message = err.response?.data?.error ?? err.message ?? 'An error occurred';
    return Promise.reject(new Error(message));
  },
);

// ─── User APIs ────────────────────────────────────────────────────────────────

export const getUsers = (params?: Record<string, string>) =>
  api.get('/users', { params }).then((r) => r.data.data);

export const getUser = (userId: string) =>
  api.get(`/users/${userId}`).then((r) => r.data.data);

// Bulk-resolve user IDs. Use when the caller has known userIds (e.g. from
// assignment tables) and the broad getUsers would filter them out due to
// role-scoped visibility. The backend caps each call at 100 IDs, so we chunk
// larger sets into parallel batches and merge — a supervisor's subtree can
// easily exceed 100 members, and a single over-cap call would 400 and leave
// every name unresolved ("Unknown").
type LookupUser = {
  userId: string;
  email: string;
  displayName: string;
  role: string;
  approvalStatus: string;
  supervisorId?: string;
};

const LOOKUP_CHUNK = 100;

export const lookupUsers = async (
  userIds: string[],
): Promise<{ users: LookupUser[]; count: number }> => {
  if (userIds.length === 0) return { users: [], count: 0 };
  const chunks: string[][] = [];
  for (let i = 0; i < userIds.length; i += LOOKUP_CHUNK) {
    chunks.push(userIds.slice(i, i + LOOKUP_CHUNK));
  }
  const results = await Promise.all(
    chunks.map((ids) =>
      api.post('/users/lookup', { userIds: ids }).then((r) => r.data.data as { users: LookupUser[] }),
    ),
  );
  const users = results.flatMap((r) => r.users ?? []);
  return { users, count: users.length };
};

export const approveUser = (userId: string, role = 'enumerator') =>
  api.put(`/users/${userId}/approve`, { role }).then((r) => r.data.data);

export const rejectUser = (userId: string, reason: string) =>
  api.put(`/users/${userId}/reject`, { reason }).then((r) => r.data.data);

export const disableUser = (userId: string, action: 'disable' | 'enable') =>
  api.put(`/users/${userId}/disable`, { action }).then((r) => r.data.data);

// Admin-only: change a user's role between enumerator / supervisor / admin / sub-admin.
// Keeps Cognito group + custom:role + DynamoDB record in sync, and prunes
// any stale supervisor_assignments rows. Promoting to supervisor auto-mints
// a code; demoting from supervisor nulls it out.
export const setUserRole = (
  userId: string,
  role: 'enumerator' | 'transcriber' | 'supervisor' | 'sub-admin' | 'admin',
) =>
  api.put(`/users/${userId}/role`, { role }).then((r) => r.data.data);

// Supervisor-only: approve a pending user who registered under this supervisor's
// code. Picks Enumerator or Transcriber. Backend enforces the supervisor
// owns the user (user.supervisorId === requestingSupervisor.userId).
export const supervisorApproveUser = (userId: string, role: 'enumerator' | 'transcriber') =>
  api.put(`/users/${userId}/supervisor-approve`, { role }).then((r) => r.data.data);

// Supervisor: flip one of the supervisor's own team members between Enumerator
// and Transcriber. Hits the shared /users/{id}/role endpoint, which now accepts
// supervisors and enforces ownership (user.supervisorId === me), restricts them
// to enumerator⇄transcriber, reconciles Cognito + assignment tables, and signs
// the member out. (Same endpoint admins use; the backend branches on caller.)
export const supervisorSetMemberRole = (userId: string, role: 'enumerator' | 'transcriber') =>
  api.put(`/users/${userId}/role`, { role }).then((r) => r.data.data);

// Supervisor: disable (revoke access) or re-enable a team member via the shared
// /users/{id}/disable endpoint. There is no hard delete — disabling is the
// reversible "remove" primitive. Backend enforces user.supervisorId === me.
export const supervisorSetMemberStatus = (userId: string, action: 'disable' | 'enable') =>
  api.put(`/users/${userId}/disable`, { action }).then((r) => r.data.data);

// ─── Supervisor Code APIs ────────────────────────────────────────────────────

export const getSupervisorCode = (supervisorId: string) =>
  api.get(`/supervisor-codes/${supervisorId}`).then((r) => r.data.data);

// Admin sets (or replaces) a supervisor's code with one they pick. Code is
// uppercased server-side, must be 4–20 alphanumeric chars, and must be unique
// across supervisors (server returns 409 otherwise).
export const setSupervisorCode = (supervisorId: string, code: string) =>
  api.put(`/supervisor-codes/${supervisorId}`, { code }).then((r) => r.data.data);

// Admin-only: flip the per-user "Upload from Gallery" capability. The
// backend also revokes the user's current JWT so the mobile app picks up
// the new permission on next login.
export const setUserGalleryUpload = (userId: string, allowed: boolean) =>
  api.put(`/users/${userId}/gallery-upload`, { allowed }).then((r) => r.data.data);

export const createUser = (data: unknown) =>
  api.post('/users', data).then((r) => r.data.data);

// ─── Project APIs ─────────────────────────────────────────────────────────────

// ─── Phone SMS-OTP (public / pre-auth) ─────────────────────────────────────────
export const phoneLookup = (phone: string): Promise<{ exists: boolean; username?: string }> =>
  api.post('/auth/phone/lookup', { phone }).then((r) => r.data.data);

export const phoneRegister = (data: {
  displayName: string;
  email: string;
  phone: string;
  password: string;
  supervisorId: string;
}) => api.post('/auth/phone/register', data).then((r) => r.data.data);

export const phoneVerifyRegister = (phone: string, code: string) =>
  api.post('/auth/phone/verify-register', { phone, code }).then((r) => r.data.data);

export const phoneResend = (phone: string, purpose = 'register') =>
  api.post('/auth/phone/resend', { phone, purpose }).then((r) => r.data.data);

// Resolve a supervisor code → { supervisorId, supervisorName } (null on invalid).
export const lookupSupervisorCode = (
  code: string,
): Promise<{ supervisorId: string; supervisorName?: string } | null> =>
  api
    .post('/supervisor-codes/lookup', { code })
    .then((r) => r.data.data)
    .catch(() => null);

export const getProjects = (params?: Record<string, string>) =>
  api.get('/projects', { params }).then((r) => r.data.data);

export const getProject = (projectId: string) =>
  api.get(`/projects/${projectId}`).then((r) => r.data.data);

export const createProject = (data: unknown) =>
  api.post('/projects', data).then((r) => r.data.data);

export const updateProject = (projectId: string, data: unknown) =>
  api.put(`/projects/${projectId}`, data).then((r) => r.data.data);

// ─── Session APIs ─────────────────────────────────────────────────────────────

export const getSessions = (params?: Record<string, string>) =>
  api.get('/sessions', { params }).then((r) => r.data.data);

export const getSession = (sessionId: string, includeUrls = false) =>
  api
    .get(`/sessions/${sessionId}`, { params: { includeUrls: includeUrls ? 'true' : 'false' } })
    .then((r) => r.data.data);

export const deleteSession = (sessionId: string) =>
  api.delete(`/sessions/${sessionId}`).then((r) => r.data.data);

// Admin-only: overwrite the primary language for a session (typo fix from
// the field). Accepted even on completed sessions.
export const updateSessionLanguage = (sessionId: string, language: string) =>
  api.put(`/sessions/${sessionId}/language`, { language }).then((r) => r.data.data);

// Admin-only: reassign a recording to a different project (fix a
// miscategorized upload). Updates the task rows + session; files aren't moved.
export const updateSessionProject = (sessionId: string, projectId: string) =>
  api.put(`/sessions/${sessionId}/project`, { projectId }).then((r) => r.data.data);

export const bulkDeleteSessions = (sessionIds: string[]) =>
  api.post('/sessions/bulk-delete', { sessionIds }).then((r) => r.data.data);

export const getSessionDownloadUrls = (sessionIds: string[]) =>
  api.post('/sessions/download-urls', { sessionIds }).then((r) => r.data.data);

// ─── Training-data export APIs (admin) ────────────────────────────────────────
export interface ExportJob {
  jobId: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  statusFilter: 'submitted' | 'approved';
  preset: string;
  projectId: string | null;
  language: string | null;
  startDate: string | null;
  endDate: string | null;
  clipCount: number | null;
  tasksTotal: number | null;
  tasksDone: number | null;
  workerId: string | null;
  heartbeatAt: string | null;
  totalBytes: number | null;
  totalSeconds: number | null;
  errorMessage: string | null;
  triggeredAt: string;
  completedAt: string | null;
  filesDeletedAt: string | null;
  bucket: string;
  prefix: string;
  s3Uri: string;
}

export const triggerExport = (body: {
  statusFilter: 'submitted' | 'approved';
  preset?: string;
  projectId?: string;
  language?: string;
  startDate?: string;
  endDate?: string;
  includeVideos?: boolean;
}): Promise<{ jobId: string; status: string }> =>
  api.post('/admin/exports', body).then((r) => r.data.data);

// Re-queue an existing export row (same jobId, same S3 prefix). Exports drop
// their SQS message as soon as a worker claims the job in the DB — they outlive
// SQS's 12h visibility ceiling — so there is no automatic retry and a failed or
// abandoned run is resubmitted here.
export const retryExport = (jobId: string): Promise<{ jobId: string; status: string }> =>
  api.post('/admin/exports', { retryJobId: jobId }).then((r) => r.data.data);

export const listExports = (): Promise<ExportJob[]> =>
  api.get('/admin/exports').then((r) => r.data.data.jobs ?? []);

// Admin-only: delete an export's S3 artifacts (clips + manifests). The job row
// is kept for history; source recordings under raw/ are never touched.
export const deleteExportFiles = (jobId: string): Promise<{ objectsDeleted: number }> =>
  api.delete(`/admin/exports/${jobId}`).then((r) => r.data.data);

export default api;

export interface LanguageSettings { languages: string[]; version: number }
export const getLanguageSettings = (): Promise<LanguageSettings> =>
  api.get('/settings/languages').then(r => r.data.data);
export const updateLanguageSettings = (data: LanguageSettings): Promise<LanguageSettings> =>
  api.put('/settings/languages', data).then(r => r.data.data);
